// Replies quote the whole conversation back at us. Gmail, Outlook, Apple Mail
// and Yahoo each wrap that history in their own markup, so collapsing it means
// recognising all of them.
const QUOTE_SELECTORS = [
  ".gmail_quote",
  ".gmail_quote_container",
  ".yahoo_quoted",
  ".moz-cite-prefix",
  'blockquote[type="cite"]',
];

// Outlook marks the divider ahead of the quoted history instead of wrapping it,
// so everything following the marker belongs to the history too.
// .OutlookMessageHeader holds only the From:/Sent:/To: block, with the history
// beside it rather than inside it, so it divides the same way.
// #appendonsend is deliberately absent: Outlook writes it into new messages as
// well, where what follows it is the sender's signature rather than history.
const OUTLOOK_DIVIDER_SELECTORS = ["#divRplyFwdMsg", ".OutlookMessageHeader"];

export const QUOTE_ATTRIBUTE = "data-inbox-quote";

export const COLLAPSED_CLASS = "inboxQuoteCollapsed";

// "On 15 Sep 2026, … wrote:" and "tis 15 sep. 2026 … skrev Tara <t@x.se>:" —
// the verb sits mid line and the sender trails it, so anchor on the colon and
// keep what lies between the two.
const ATTRIBUTION_PATTERN =
  /\b(wrote|skrev|sent|skickade|schrieb|a écrit)\b([^\n]*?):\s*$/i;

// Between the verb and the colon a client writes the sender and nothing else —
// an address in angle brackets, a name, or nothing at all where the name runs
// ahead of the verb. Prose that merely ends in a verb and a colon trails off in
// lowercase instead: "was sent on 2026-09-14 to the following:".
const SENDER_TAIL = /(^$|>$|@[^\s@]+$|[A-ZÅÄÖÆØÜÉÈ][^\s]*$)/;

// Clients open the line with the date, or with the word that introduces it —
// "On Tue, …", "Den 14 sep …", "tis 15 sep. …", "Am 15.09.2026 …". Requiring
// that keeps a reply sharing an element with its own attribution line from
// matching: textContent flattens the <br> between them into one string, and
// without an anchor at the front the sender's words are hidden along with it.
const ATTRIBUTION_START =
  /^(\d|on\b|den\b|le\b|am\b|[A-Za-zÅÄÖÜåäöü.]{2,5}[\s,]+\d)/i;

// Every client also stamps the line with when the quote was written or who
// wrote it. A bare number is neither — "wrote in journal 4471:" is a sentence.
const ATTRIBUTION_EVIDENCE = /\d{1,4}[\s./:-]\d|[^\s@]+@[^\s@]+\.[a-z]/i;

const isAttributionLine = (text) => {
  const match = ATTRIBUTION_PATTERN.exec(text);

  return Boolean(
    match &&
      ATTRIBUTION_START.test(text) &&
      SENDER_TAIL.test(match[2].trim()) &&
      ATTRIBUTION_EVIDENCE.test(text)
  );
};

// Apple Mail puts a <div><br></div> spacer between the attribution line and the
// quote it introduces, so the line is not always the immediate sibling.
const previousWrittenSibling = (node) => {
  let previous = node.previousElementSibling;
  while (previous && !(previous.textContent || "").trim()) {
    previous = previous.previousElementSibling;
  }

  return previous;
};

// Gmail keeps the "On … wrote:" line outside the quote, and hiding the quote
// without it leaves a dangling sentence behind.
const markAttributionLine = (node) => {
  const previous = previousWrittenSibling(node);
  if (!previous) return;

  const text = previous.textContent.trim();
  if (text.length > 300) return;
  if (!isAttributionLine(text)) return;

  previous.setAttribute(QUOTE_ATTRIBUTE, "true");
};

// A clone carries no layout, so an image can only be judged by what it declares.
// Tracking pixels and spacer gifs declare a side of a pixel or two; counting one
// as content collapses a bare forward into an all but empty frame.
const SPACER_IMAGE_SIDE = 8;

const isContentImage = (img) => {
  const width = Number(img.getAttribute("width"));
  const height = Number(img.getAttribute("height"));

  return !(
    (width && width <= SPACER_IMAGE_SIDE) ||
    (height && height <= SPACER_IMAGE_SIDE)
  );
};

// What the email still shows once the marked history is hidden. Cloning is
// cheaper to read than walking the tree and runs once per email.
const hasUnquotedContent = (body) => {
  const clone = body.cloneNode(true);
  clone
    .querySelectorAll(`[${QUOTE_ATTRIBUTE}]`)
    .forEach((node) => node.remove());

  if (clone.textContent.trim()) return true;

  return Array.from(clone.querySelectorAll("img")).some(isContentImage);
};

export const markQuotedNodes = (doc) => {
  const body = doc?.body;
  if (!body) return false;

  const quoted = [];

  QUOTE_SELECTORS.forEach((selector) => {
    body.querySelectorAll(selector).forEach((node) => quoted.push(node));
  });

  OUTLOOK_DIVIDER_SELECTORS.forEach((selector) => {
    const divider = body.querySelector(selector);
    if (!divider) return;

    quoted.push(divider);
    // Outlook wraps the divider a level or two down as often as not, and the
    // history it divides is then a sibling of the wrapper rather than of the
    // divider, so climb to the body collecting what follows at each level.
    for (let node = divider; node && node !== body; node = node.parentElement) {
      let sibling = node.nextElementSibling;
      while (sibling) {
        quoted.push(sibling);
        sibling = sibling.nextElementSibling;
      }
    }
  });

  quoted.forEach((node) => {
    // A quote nested inside another quote is already hidden with its parent.
    if (node.closest(`[${QUOTE_ATTRIBUTE}]`)) return;

    node.setAttribute(QUOTE_ATTRIBUTE, "true");
    markAttributionLine(node);
  });

  if (!body.querySelector(`[${QUOTE_ATTRIBUTE}]`)) return false;

  // A bare forward, or a reply written below the history, is quoted top to
  // bottom. Hiding all of it leaves an empty frame behind, so such an email is
  // better shown whole — the same call the plain text split makes.
  if (!hasUnquotedContent(body)) {
    body
      .querySelectorAll(`[${QUOTE_ATTRIBUTE}]`)
      .forEach((node) => node.removeAttribute(QUOTE_ATTRIBUTE));

    return false;
  }

  return true;
};

const TEXT_QUOTE_MARKERS = [
  /^-{2,}\s*(original message|ursprungligt meddelande|forwarded message|vidarebefordrat meddelande)\s*-{2,}\s*$/i,
  /^_{5,}\s*$/,
];

// Quoted history runs on to the end of the email. A lone ">" line with the
// sender's own prose under it is a bullet or a pasted command instead, and
// splitting there would hide the rest of what they wrote.
const startsQuotedBlock = (lines, index) => {
  if (!/^>/.test(lines[index].trim())) return false;

  const next = lines.slice(index + 1).find((line) => line.trim());

  return !next || /^>/.test(next.trim());
};

// A forwarded header block opens with From:/Från: carrying the address and the
// rest of the block follows it. A lone "From: Anna Berg" is what a contact form
// wrote into the body, and treating that as history hides the whole errand.
const HEADER_BLOCK_LEAD = /^(from|från):\s*\S.*[@<]/i;

const HEADER_BLOCK_FOLLOW =
  /^(sent|skickat|date|datum|to|till|cc|subject|ämne):\s/i;

const startsHeaderBlock = (lines, index) =>
  HEADER_BLOCK_LEAD.test(lines[index].trim()) &&
  lines
    .slice(index + 1, index + 4)
    .some((line) => HEADER_BLOCK_FOLLOW.test(line.trim()));

// Plain text replies carry no markup at all, so the history has to be found by
// the attribution line the sending client wrote above it.
export const splitQuotedText = (text) => {
  const lines = String(text || "").split(/\r?\n/);
  const quoteStart = lines.findIndex((line, index) => {
    const trimmed = line.trim();
    if (!trimmed) return false;

    return (
      TEXT_QUOTE_MARKERS.some((marker) => marker.test(trimmed)) ||
      startsQuotedBlock(lines, index) ||
      isAttributionLine(trimmed) ||
      startsHeaderBlock(lines, index)
    );
  });

  if (quoteStart < 0) return { visible: String(text || ""), quoted: "" };

  const visible = lines.slice(0, quoteStart).join("\n");
  // Nothing is gained by collapsing an email that is quoted history top to
  // bottom — it would leave an empty card behind.
  if (!visible.trim()) return { visible: String(text || ""), quoted: "" };

  return {
    visible: visible.replace(/\s+$/, ""),
    quoted: lines.slice(quoteStart).join("\n"),
  };
};
